import { Link, usePage, router } from '@inertiajs/react'
import type { SharedProps } from '@/types'
import {
  ArrowRightLeft,
  FolderOpen,
  FileText,
  BarChart3,
  Download,
  Settings,
  LogOut,
  Database,
  Inbox,
  Menu,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet'
import { useState, useEffect } from 'react'

const accountingNavigation = [
  { name: 'Inbox', href: '/reimbursement_items', icon: Inbox },
  { name: 'Transactions', href: '/transactions', icon: ArrowRightLeft },
  { name: 'Invoices', href: '/invoices', icon: FileText },
  { name: 'Cash Flow', href: '/statistics', icon: BarChart3 },
]

const secondaryNavigation = [
  { name: 'Categories', href: '/categories', icon: FolderOpen },
  { name: 'Exports', href: '/exports', icon: Download },
  { name: 'Backups', href: '/backups', icon: Database },
]

function SidebarContent() {
  const { url, props } = usePage<SharedProps>()
  const { auth } = props

  function isActive(href: string) {
    if (href === '/transactions') {
      return url.startsWith('/transactions')
    }
    return url.startsWith(href)
  }

  const initials = auth?.user?.email
    ? auth.user.email.substring(0, 2).toUpperCase()
    : 'U'

  return (
    <>
      {/* Logo */}
      <div className="flex h-14 items-center gap-2.5 px-5">
        <Link href="/" className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-sidebar-primary">
            <span className="text-xs font-bold text-sidebar-primary-foreground">N</span>
          </div>
          <span className="text-[15px] font-semibold tracking-tight text-sidebar-foreground">
            Nest
          </span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-0.5 px-3 pt-2">
        <p className="mb-2 px-3 text-[11px] font-medium uppercase tracking-wider text-sidebar-foreground/40">
          Accounting
        </p>
        {accountingNavigation.map((item) => {
          const active = isActive(item.href)
          return (
            <Link
              key={item.name}
              href={item.href}
              className={cn(
                'group flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors',
                active
                  ? 'bg-sidebar-accent text-sidebar-primary'
                  : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
              )}
            >
              <item.icon
                className={cn(
                  'h-4 w-4 shrink-0',
                  active ? 'text-sidebar-primary' : 'text-sidebar-foreground/50 group-hover:text-sidebar-foreground/70'
                )}
              />
              {item.name}
            </Link>
          )
        })}

        <div className="my-3 mx-3 border-t border-sidebar-border" />

        <p className="mb-2 px-3 text-[11px] font-medium uppercase tracking-wider text-sidebar-foreground/40">
          System
        </p>
        {secondaryNavigation.map((item) => {
          const active = isActive(item.href)
          return (
            <Link
              key={item.name}
              href={item.href}
              className={cn(
                'group flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors',
                active
                  ? 'bg-sidebar-accent text-sidebar-primary'
                  : 'text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground'
              )}
            >
              <item.icon
                className={cn(
                  'h-4 w-4 shrink-0',
                  active ? 'text-sidebar-primary' : 'text-sidebar-foreground/50 group-hover:text-sidebar-foreground/70'
                )}
              />
              {item.name}
            </Link>
          )
        })}
      </nav>

      {/* User */}
      <div className="border-t border-sidebar-border p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors hover:bg-sidebar-accent/60">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-sidebar-accent text-[11px] font-semibold text-sidebar-foreground">
                {initials}
              </div>
              <div className="flex-1 truncate text-[13px] font-medium text-sidebar-foreground/80">
                {auth?.user?.email || 'User'}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-52">
            <DropdownMenuItem asChild>
              <Link href="/sessions" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                Sessions
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link
                href="/sign_out"
                method="delete"
                as="button"
                className="flex w-full items-center gap-2 text-destructive"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  )
}

export function Sidebar() {
  const [open, setOpen] = useState(false)

  // Auto-close mobile sidebar on navigation
  useEffect(() => {
    const removeListener = router.on('navigate', () => {
      setOpen(false)
    })
    return removeListener
  }, [])

  return (
    <>
      {/* Mobile top bar */}
      <div className="fixed top-0 left-0 right-0 z-40 flex h-14 items-center gap-2.5 border-b border-sidebar-border bg-sidebar px-4 md:hidden">
        <button
          onClick={() => setOpen(true)}
          className="rounded-lg p-2 text-sidebar-foreground/70 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground transition-colors"
        >
          <Menu className="h-5 w-5" />
        </button>
        <Link href="/" className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-sidebar-primary">
            <span className="text-xs font-bold text-sidebar-primary-foreground">N</span>
          </div>
          <span className="text-[15px] font-semibold tracking-tight text-sidebar-foreground">
            Nest
          </span>
        </Link>
      </div>

      {/* Mobile sidebar drawer */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-[240px] p-0 bg-sidebar border-sidebar-border" showCloseButton={false}>
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="flex h-full flex-col">
            <SidebarContent />
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop sidebar */}
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-[240px] flex-col border-r border-sidebar-border bg-sidebar md:flex">
        <SidebarContent />
      </aside>
    </>
  )
}

export function SidebarSpacer() {
  return <div className="hidden md:block md:w-[240px] shrink-0" />
}
